// src/stores/roleStore.js
import { defineStore } from 'pinia'
import RoleService from '@/services/roleService'
import { toast } from '@/utils/toast'
import { MODULE_KEYS } from "@/config/modules-registry";
import { guardPlanModule } from "@/utils/planAccess";

export const useRoleStore = defineStore('role', {
  state: () => ({
    roles: [],
    loading: false,
    error: null,
  }),

  actions: {
    // Get all roles
    async fetchRoles() {
      if (!(await guardPlanModule(MODULE_KEYS.ADMIN_SETTINGS))) {
        this.roles = [];
        return;
      }

      this.loading = true
      this.error = null
      try {
        const response = await RoleService.getAll()
        // Optionally map status if not present
        this.roles = response.data.map(role => ({
          ...role,
          status: role.status || 'Active'
        }))
      } catch (error) {
        this.error = error.response?.data?.message || 'Failed to fetch roles'
        toast(this.error, 'error')
      } finally {
        this.loading = false
      }
    },

    // Create a new role
    async createRole(roleData) {
      this.loading = true
      this.error = null
      try {
        const response = await RoleService.create(roleData)
        this.roles.push(response.data)
        toast('Role created successfully')
        return response.data
      } catch (error) {
        this.error = error.response?.data?.message || 'Failed to create role'
        toast(this.error, 'error')
        throw error
      } finally {
        this.loading = false
      }
    },

    // Update existing role
    async updateRole(roleId, roleData) {
      this.loading = true
      this.error = null
      try {
        const response = await RoleService.update(roleId, roleData)
        const idx = this.roles.findIndex(r => r.id === roleId)
        if (idx !== -1) this.roles[idx] = response.data
        toast('Role updated successfully')
        return response.data
      } catch (error) {
        this.error = error.response?.data?.message || 'Failed to update role'
        toast(this.error, 'error')
        throw error
      } finally {
        this.loading = false
      }
    },

    // Delete a role
    async deleteRole(roleId) {
      this.loading = true
      this.error = null
      try {
        await RoleService.delete(roleId)
        this.roles = this.roles.filter(r => r.id !== roleId)
        toast('Role deleted successfully')
      } catch (error) {
        this.error = error.response?.data?.message || 'Failed to delete role'
        toast(this.error, 'error')
        throw error
      } finally {
        this.loading = false
      }
    },

    // Assign permissions to role
    async assignPermissions(roleId, permissions) {
      this.loading = true
      this.error = null
      try {
        await RoleService.assignPermissions(roleId, permissions)
        await this.fetchRoles()
        toast('Permissions updated successfully!')
      } catch (error) {
        this.error = error.response?.data?.message || 'Failed to update permissions'
        toast(this.error, 'error')
        throw error
      } finally {
        this.loading = false
      }
    }
  }
})
